import React, { useEffect, useState } from 'react';
import { useNavigation, useIsFocused, useRoute } from '@react-navigation/native';
import { View, KeyboardAvoidingView, StatusBar, useWindowDimensions, ScrollView, BackHandler, TouchableOpacity } from 'react-native';
import { ThemeConsumer } from '@rneui/themed';
import { appColors, appFonts, fontSize } from '../../themes/appTheme';
import { config } from '../../shared/environment';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { DialogBoxType, Menu, NavigationProps, PosPermissionTypeProps, getOneRoleResponse, itemResProps, menuListResProps, routeProps, subMenuResProps, updatePosRoleProps, updateValueProps } from './roleModule';
import { getScreenPermission, useAppDispatch, useAppSelector } from '../../redux/hooks';
import { createRole, onePosRole, roleMenuPermission, updateRole } from '../../redux/slices/rolesSlice';
import { ZenButton, ZenCheckBox, ZenDivider, ZenIcon, ZenInput, ZenLoader, ZenText, ZenToast } from 'zen-core-elements';
import Popover from 'react-native-popover-view';
import CommonDialogBox from '../../shared/commonComponents/commonDialogBox';
import CommonHeader from '../../shared/commonComponents/commonHeader';
import { shallowEqual } from 'react-redux';
import { getOnePostDuplicateCheck } from '../../redux/slices/optionSlice';


const READ_ONLY_MENUS = [
    'dashboard',
    'total hours worked',
    'mail logs',
    'discount logs',
    'purchased gift card',
    'invited staff',
];

const isReadOnlyMenu = (menuName?: string): boolean => {
    if (!menuName) return false;
    return READ_ONLY_MENUS.includes(menuName.trim().toLowerCase());
};

//A screen can only be granted when the catalogue (GET /roles/permission) lists permission types for it.
//The permission type ids (posPermissionTypes[].id) are what the API expects as permissionId, never the menu id
const hasPermissionTypes = (menu?: { posPermissionTypes?: PosPermissionTypeProps[] }): boolean => (menu?.posPermissionTypes?.length ?? 0) > 0;

//Select all state of a parent menu, worked out from the sub menus that can be granted
const summariseSubMenus = (subMenus: subMenuResProps[]) => {
    const grantable = subMenus.filter((sub) => hasPermissionTypes(sub));
    const writable = grantable.filter((sub) => !isReadOnlyMenu(sub.menuName));
    return {
        selectAllRead: grantable.length > 0 && grantable.every((sub) => sub.isRead),
        selectAllWrite: writable.length > 0 && writable.every((sub) => sub.isWrite),
        selectAll: grantable.length > 0 && grantable.every((sub) => isReadOnlyMenu(sub.menuName) ? sub.isRead : (sub.isRead && sub.isWrite)),
    };
};

function AddEditRole(): React.JSX.Element {
    const { isWrite: isRoleWrite } = useAppSelector(getScreenPermission("RoleStack"), shallowEqual)

    const dispatch = useAppDispatch()
    const { width, height } = useWindowDimensions();
    const route = useRoute<routeProps>()
    const roleId: number = route?.params?.id
    const isEdit: boolean = route?.params?.isEdit
    const navigation = useNavigation<NavigationProps>();
    // Used to show/hide the loader
    const [isLoad, setIsLoad] = useState<boolean>(false);
    // Variable to hold the formatted role value details
    const [roleDetails, setRoleDetails] = useState<getOneRoleResponse["roles"][0]>();
    // Variable to store the list of menus
    const [menuList, setMenuList] = useState<menuListResProps[]>([]);
    const [confirmation, setConfirmation] = useState<DialogBoxType | null>(null)
    const [dialogVisible, setDialogVisible] = useState(false)

    //State varible to handle the visibility of submenu
    const [isSubMenuOpen, setIsSubMenuOpen] = useState<{ id: number, value: boolean }[]>([])

    const { value: duplicateCheckValue, loading: duplicateCheckLoading } = useAppSelector(state => state.optionSlice?.getOnePostDuplicateCheck)
    const menuPermissionList = useAppSelector(state => state.roles?.roleMenuPermission?.menus)

    // Validation for roleName
    const validationSchema = Yup.object().shape({
        roleName: Yup.string()
            .required('This field is required')
            .max(35, "Role name should not exceed 35 characters!"),
        // .matches(/^([a-zA-Z]+\s)*[a-zA-Z]+$/, 'Role name accepts characters only.',),
        description: Yup.string()
            .max(150, 'Description should not exceed 150 characters.')
            .matches(/(?!^ +$)^.+$/, 'Please provide a valid description')
    })
    // Formik for maintaing state of the variables
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            roleName: roleDetails && roleDetails.name ? roleDetails.name : '',
            description: roleDetails && roleDetails?.description ? roleDetails?.description : '',
            menuList: menuList ? menuList : [],
        },
        validationSchema: validationSchema,
        onSubmit: () => {
            createPosRole();
        }
    }
    )
    // Initial useEffect
    useEffect(() => {
        setIsLoad(true)
        if (isEdit)
            getOnePosRole()
        else
            getAllPosRoleMenuPermission([]);
    }, []);

    //UseEffect function which is used to perform backhandler operation
    useEffect(() => {
        BackHandler.addEventListener('hardwareBackPress', function () {
            if (formik?.dirty)
                setDialogVisible(true)
            else
                navigation.goBack()
            return true
        })
    }, [formik?.dirty])

    useEffect(() => {
        if (confirmation && confirmation.id == 2 && dialogVisible) {
            setConfirmation(null)
            navigation.goBack()
            setDialogVisible(false)

        }
        else if (dialogVisible) {
            setDialogVisible(false)
            setConfirmation(null)
        }
    }, [confirmation])

    useEffect(() => {

        if (formik?.values?.roleName && formik?.dirty) {
            const duplicateBounce = setTimeout(() => {
                checkRoleName(formik?.values?.roleName);
            }, 500);

            // Clear the timeout if this function is invoked again
            return () => clearTimeout(duplicateBounce);
        }
    }, [formik?.values?.roleName])


    const createPosRole = async () => {
        setIsLoad(true)
        let permissions: { permissionId: number, isRead: boolean, isWrite: boolean }[] = [];
        let menuData = [...formik.values.menuList]

        menuData?.length > 0 && menuData?.forEach((item: menuListResProps) => {
            if (item?.subMenus && item?.subMenus?.length > 0) {
                item?.subMenus?.forEach((element: subMenuResProps) => {
                    const isReadOnly = isReadOnlyMenu(element.menuName);
                    if (element.isRead || element.isWrite) {
                        element.posPermissionTypes?.forEach((posId: PosPermissionTypeProps) => {
                            permissions.push({
                                permissionId: posId.id,
                                isRead: !!element.isRead,
                                isWrite: isReadOnly ? false : !!element.isWrite
                            });
                        });
                    }
                });
            } else {
                const isReadOnly = isReadOnlyMenu(item.menuName);
                if (item.isRead || item.isWrite) {
                    item.posPermissionTypes?.forEach((posId: PosPermissionTypeProps) => {
                        permissions.push({
                            permissionId: posId.id,
                            isRead: !!item.isRead,
                            isWrite: isReadOnly ? false : !!item.isWrite
                        });
                    });
                }
            }
        })

        // The backend validator rejects null for optional strings, so an empty description is left out
        const description = formik.values.description?.trim();
        let updateValue: updateValueProps = { name: formik.values.roleName, ...(description ? { description } : {}), permissions: permissions };
        if (!isEdit) {
            await dispatch(createRole({ storeId: config?.storeId, data: updateValue })).unwrap().then((res) => {
                if (res) {
                    navigation.goBack();
                }
            }).catch((err: string) => {
                console.log('Error in creating roles', err);
                setIsLoad(false);
            })
        }
        else {
            await dispatch(updateRole({ storeId: config?.storeId, id: roleId, data: updateValue })).unwrap().then((res) => {
                if (res) {
                    navigation.goBack();
                }
            }).catch((err: string) => {
                console.log('Error in creating roles', err);
                setIsLoad(false);
            })
        }
    }
    /**
     * 
     * @param roleObj 
     * @returns 
     */
    const getAllPosRoleMenuPermission = async (roleObj: any) => {
        try {
            // Always load the full catalogue (every menu with its permission types) so newly configured
            // permissions show up; the cached copy is only a fallback when the request fails
            let activeMenuList: menuListResProps[] | undefined;
            try {
                const res = await dispatch(roleMenuPermission({ storeId: config?.storeId })).unwrap();
                activeMenuList = res?.menus;
            } catch (catalogueError) {
                console.log("Error fetching the role permission catalogue:", catalogueError);
                activeMenuList = menuPermissionList;
            }

            if (!activeMenuList || !Array.isArray(activeMenuList)) {
                setIsLoad(false);
                return;
            }

            const dropDownData = JSON.parse(JSON.stringify(activeMenuList));
            let menuVisibilty: { id: number, value: boolean }[] = []

            // Permissions already assigned to the role, keyed by permission id (menus[].permissions[] and
            // menus[].subMenus[].permissions[] of GET /roles/{roleId}). Only assigned permissions are listed there,
            // so the catalogue stays the source of the screens and their permission ids
            const assignedPermissions = new Map<number, { isRead: boolean, isWrite: boolean }>();
            const collectAssigned = (menus?: any[]) => {
                menus?.forEach((menu) => {
                    menu?.permissions?.forEach((permission: any) => {
                        if (typeof permission?.permissionId === "number") {
                            assignedPermissions.set(permission.permissionId, { isRead: !!permission.isRead, isWrite: !!permission.isWrite });
                        }
                    });
                    collectAssigned(menu?.subMenus);
                });
            };
            if (isEdit) collectAssigned(roleObj?.menus);

            //Read/write state of a catalogue menu, matched only by its permission type ids
            const getMenuSettings = (menu: { menuName: string, posPermissionTypes?: PosPermissionTypeProps[] }) => {
                let isRead = false, isWrite = false;
                menu.posPermissionTypes?.forEach((pos) => {
                    const assigned = assignedPermissions.get(pos.id);
                    if (assigned) {
                        isRead = isRead || assigned.isRead;
                        isWrite = isWrite || assigned.isWrite;
                    }
                });
                if (isReadOnlyMenu(menu.menuName)) isWrite = false;
                return { isRead: isRead || isWrite, isWrite };
            };

            dropDownData?.forEach((item: any) => {
                menuVisibilty.push({ id: item?.id, value: false })
                if (item?.subMenus?.length > 0) {
                    item.subMenus.forEach((sub: any) => {
                        const { isRead, isWrite } = getMenuSettings(sub);
                        sub.isRead = isRead;
                        sub.isWrite = isWrite;
                        sub.value = isRead && isWrite;
                    });
                    Object.assign(item, summariseSubMenus(item.subMenus));
                } else {
                    const { isRead, isWrite } = getMenuSettings(item);
                    item.isRead = isRead;
                    item.isWrite = isWrite;
                    item.selectAll = isRead && isWrite;
                }
            });
            setIsSubMenuOpen(menuVisibilty)
            setMenuList(dropDownData);
            setTimeout(() => setIsLoad(false), 0)
        } catch (err) {
            console.log("Error fetching role menu permissions:", err);
        }
    };
    /**
     * 
     */
    const getOnePosRole = async () => {
        await dispatch(onePosRole({ storeId: config?.storeId, id: roleId })).unwrap().then((res) => {
            console.log(JSON.stringify(res, undefined, 4));

            if (res && res?.roles) {
                setRoleDetails(res?.roles[0])
                getAllPosRoleMenuPermission(res?.roles?.[0])
            }
            else {
                getAllPosRoleMenuPermission(undefined)
            }

        }).catch((err: string) => {
            console.log("errr", err)
        })
    }
    /** 
     * 
     * @param roleText 
     */
    const checkRoleName = async (roleText: string) => {
        if (roleText) {
            await dispatch(getOnePostDuplicateCheck(
                { data: { columnName: "name", id: roleId, storeId: true, tableName: "posRoles", value: roleText.trim() }, storeId: config.storeId })).unwrap().catch((err) => {
                    console.log("err in duplicateCheck", err);
                })
        }
    }
    /**
     * 
     * @param arrayIndex 
     * @param selectAllState 
     */
    const toggleParentSelectAllRead = (arrayIndex: number, selectAllReadState: boolean) => {
        const updatedMenuList = formik.values.menuList.map((item, index) => {
            if (index !== arrayIndex) return item;

            const updatedSubMenus = item.subMenus?.map(sub => {
                if (!hasPermissionTypes(sub)) return sub;
                const isRead = selectAllReadState;
                const isWrite = (isRead && !isReadOnlyMenu(sub.menuName)) ? sub.isWrite : false;
                return {
                    ...sub,
                    isRead,
                    isWrite,
                    value: isRead && isWrite
                };
            }) ?? [];

            return {
                ...item,
                ...summariseSubMenus(updatedSubMenus),
                subMenus: updatedSubMenus
            };
        });

        formik.setFieldValue('menuList', updatedMenuList);
    };

    const toggleParentSelectAllWrite = (arrayIndex: number, selectAllWriteState: boolean) => {
        const updatedMenuList = formik.values.menuList.map((item, index) => {
            if (index !== arrayIndex) return item;

            const updatedSubMenus = item.subMenus?.map(sub => {
                if (!hasPermissionTypes(sub)) return sub;
                const isWrite = isReadOnlyMenu(sub.menuName) ? false : selectAllWriteState;
                const isRead = selectAllWriteState ? true : (isWrite ? true : sub.isRead);
                return {
                    ...sub,
                    isRead,
                    isWrite,
                    value: isRead && isWrite
                };
            }) ?? [];

            return {
                ...item,
                ...summariseSubMenus(updatedSubMenus),
                subMenus: updatedSubMenus
            };
        });

        formik.setFieldValue('menuList', updatedMenuList);
    };

    const toggleParentRead = (arrayIndex: number, isReadState: boolean) => {
        const updatedMenuList = formik.values.menuList.map((item, index) => {
            if (index !== arrayIndex) return item;
            const isRead = isReadState;
            const isWrite = (isRead && !isReadOnlyMenu(item.menuName)) ? item.isWrite : false;
            return {
                ...item,
                isRead,
                isWrite,
                selectAll: isRead && isWrite
            };
        });

        formik.setFieldValue('menuList', updatedMenuList);
    };

    const toggleParentWrite = (arrayIndex: number, isWriteState: boolean) => {
        const updatedMenuList = formik.values.menuList.map((item, index) => {
            if (index !== arrayIndex) return item;
            const isWrite = isReadOnlyMenu(item.menuName) ? false : isWriteState;
            const isRead = isWrite ? true : !!item.isRead;
            return {
                ...item,
                isRead,
                isWrite,
                selectAll: isRead && isWrite
            };
        });

        formik.setFieldValue('menuList', updatedMenuList);
    };

    // Function to handle dropdown view
    const showSubMenu = (id: number) => {
        setIsSubMenuOpen(prev => {
            let newData = [...prev]
            const updatedData = newData?.map((item, index) => {
                if (item?.id == id) {
                    return {
                        ...item,
                        value: !item.value
                    }
                }
                else {
                    return item
                }
            })
            return updatedData
        })
    }

    const toggleSubmenuRead = (index: number, subMenuIndex: number, isReadState: boolean) => {
        let menu = formik.values.menuList.map((item, i) => {
            if (i !== index) return item;

            const newSubMenus = item.subMenus?.map((sub, sIndex) => {
                if (sIndex !== subMenuIndex) return sub;
                const isRead = isReadState;
                const isWrite = (isRead && !isReadOnlyMenu(sub.menuName)) ? sub.isWrite : false;
                return {
                    ...sub,
                    isRead,
                    isWrite,
                    value: isRead && isWrite
                };
            }) ?? [];

            return {
                ...item,
                subMenus: newSubMenus,
                ...summariseSubMenus(newSubMenus)
            };
        });

        formik.setFieldValue('menuList', menu);
    };

    const toggleSubmenuWrite = (index: number, subMenuIndex: number, isWriteState: boolean) => {
        let menu = formik.values.menuList.map((item, i) => {
            if (i !== index) return item;

            const newSubMenus = item.subMenus?.map((sub, sIndex) => {
                if (sIndex !== subMenuIndex) return sub;
                const isWrite = isReadOnlyMenu(sub.menuName) ? false : isWriteState;
                const isRead = isWrite ? true : !!sub.isRead;
                return {
                    ...sub,
                    isRead,
                    isWrite,
                    value: isRead && isWrite
                };
            }) ?? [];

            return {
                ...item,
                subMenus: newSubMenus,
                ...summariseSubMenus(newSubMenus)
            };
        });

        formik.setFieldValue('menuList', menu);
    };

    const checkIsDirty = () => {
        if (formik.dirty) {
            setDialogVisible(true)
        }
        else {
            navigation.goBack()
        }
    }

    return (
        <ThemeConsumer>
            {({ theme }: { theme: any }) => {
                return (
                    <KeyboardAvoidingView style={{ flex: 1, backgroundColor: appColors.lightBackgroundColor }}>
                        <StatusBar backgroundColor={appColors.primary} />
                        <View style={{ flex: 1 }}>
                            <CommonHeader backIcon backIconOnpress={() => { checkIsDirty() }} headerName={isEdit ? 'Edit Role' : 'Create Role'}
                                rightComponent={
                                    <View style={{ flexDirection: 'row', marginHorizontal: 10, marginVertical: 15, alignSelf: "flex-end" }}>
                                        <ZenButton
                                            title='Save'
                                            type='solid'
                                            onPress={() => {
                                                if (formik.dirty && !duplicateCheckValue?.result?.alreadyExists) {
                                                    formik.handleSubmit()
                                                }
                                                else {
                                                    ZenToast({ message: duplicateCheckValue?.result?.alreadyExists ? "the name was already taken" : "you have no changes to save", type: duplicateCheckValue?.result?.alreadyExists ? "error" : "info" })
                                                }
                                            }
                                            }
                                            buttonStyle={{ width: 100, height: 40, borderRadius: 7, backgroundColor: appColors.primary, marginLeft: 10 }}
                                            titleStyle={{ color: appColors.light, fontFamily: appFonts.bold, fontSize: fontSize.mediumFontSize }}
                                        />
                                    </View>
                                }
                            />
                            {isLoad ?
                                <ZenLoader lottieType='Local' lottie={require("../../assets/infinityLoader.json")} />
                                :
                                <ScrollView showsVerticalScrollIndicator={false} nestedScrollEnabled={true} >
                                    <View style={{ flexDirection: width > height ? 'row' : 'column', paddingBottom: width > height ? 12 : undefined, backgroundColor: appColors.lightBackgroundColor, flex: 1, justifyContent: 'space-around', alignItems: "flex-start", paddingHorizontal: 15, paddingVertical: 20, gap: 20 }}>
                                        <View style={{
                                            borderRadius: 16,
                                            width: width > height ? '44%' : "100%",
                                            backgroundColor: appColors.light,
                                            paddingVertical: 24,
                                            paddingHorizontal: 20,
                                            borderWidth: 1,
                                            borderColor: '#E2E8F0',
                                            shadowColor: '#000',
                                            shadowOffset: { width: 0, height: 2 },
                                            shadowOpacity: 0.04,
                                            shadowRadius: 10,
                                            elevation: 3
                                        }}>
                                            <View style={{ marginTop: 2, paddingHorizontal: 5 }}>
                                                <ZenInput
                                                    type='outlined'
                                                    label="Role Name *"
                                                    disabled={!isRoleWrite}
                                                    labelStyle={{
                                                        fontSize: fontSize.mediumFontSize, fontFamily: appFonts.bold, color: appColors.lightDark
                                                    }}
                                                    placeholder="Enter role name"
                                                    value={formik.values?.roleName}
                                                    onChangeText={(text: string) => {
                                                        formik.setFieldValue('roleName', text);
                                                    }}
                                                    inputContainerStyle={{ height: 45 }}
                                                    rightIcon={duplicateCheckLoading ? <ZenLoader size={"small"} color={appColors.primary} /> : null}
                                                    onBlur={() => { formik.setFieldTouched('roleName', true) }}
                                                    errorMessage={formik.touched.roleName && formik.errors.roleName ? formik.errors.roleName : duplicateCheckValue?.result?.alreadyExists ? "The name is already taken " : ""}
                                                    errorStyle={[theme.addEditProductStyle.errorStyle, { paddingBottom: formik?.errors?.roleName && !formik?.isValid && formik?.touched.roleName ? 10 : 0 }]}
                                                    keyboardType="default"
                                                />
                                                <ZenInput
                                                    type='outlined'
                                                    multiline
                                                    disabled={!isRoleWrite}
                                                    label="Description"
                                                    labelStyle={{
                                                        fontSize: fontSize.mediumFontSize, fontFamily: appFonts.bold, color: appColors.lightDark
                                                    }}
                                                    placeholder="Describe this role's purpose"
                                                    inputContainerStyle={{ height: 80 }}
                                                    value={formik.values?.description}
                                                    onBlur={() => { formik.setFieldTouched('description', true); formik.handleBlur('description'); formik.setFieldValue('description', formik?.values?.description?.trim()); }}
                                                    onChangeText={(text: string) => { formik.setFieldValue('description', text) }}
                                                    errorMessage={(formik.touched.description && formik.errors.description) ? (formik.touched.description && formik.errors.description) : undefined}
                                                    keyboardType="default"
                                                />
                                            </View>
                                        </View>

                                        <View style={{
                                            borderRadius: 16,
                                            width: width > height ? '53%' : "100%",
                                            backgroundColor: appColors.light,
                                            paddingVertical: 24,
                                            paddingHorizontal: 20,
                                            borderWidth: 1,
                                            borderColor: '#E2E8F0',
                                            shadowColor: '#000',
                                            shadowOffset: { width: 0, height: 2 },
                                            shadowOpacity: 0.04,
                                            shadowRadius: 10,
                                            elevation: 3
                                        }}>
                                            <View style={{ width: '100%' }}>
                                                {
                                                    formik?.values?.menuList?.length > 0 && formik?.values?.menuList?.map((element: menuListResProps, index: number) => {
                                                        const hasSubMenus = element?.subMenus && element?.subMenus?.length > 0;
                                                        const isOpen = isSubMenuOpen?.[index]?.value;
                                                        // Screens without permission types in the catalogue can't be granted, sending them would save nothing
                                                        const isGrantable = hasSubMenus
                                                            ? element.subMenus.some((sub: subMenuResProps) => hasPermissionTypes(sub))
                                                            : hasPermissionTypes(element);
                                                        const parentHasWritePermission = hasSubMenus
                                                            ? element.subMenus.some((sub: subMenuResProps) => hasPermissionTypes(sub) && !isReadOnlyMenu(sub.menuName))
                                                            : !isReadOnlyMenu(element.menuName);
                                                        return (
                                                            <View
                                                                style={{
                                                                    marginBottom: 16,
                                                                    borderWidth: 1,
                                                                    borderColor: '#EDF2F7',
                                                                    borderRadius: 12,
                                                                    backgroundColor: '#FFFFFF',
                                                                    overflow: 'hidden',
                                                                }}
                                                                key={index}>
                                                                <View
                                                                    style={{
                                                                        flexDirection: 'row',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'space-between',
                                                                        backgroundColor: '#F8FAFC',
                                                                        paddingVertical: 12,
                                                                        paddingHorizontal: 16,
                                                                        borderBottomWidth: hasSubMenus && isOpen ? 1 : 0,
                                                                        borderBottomColor: '#EDF2F7',
                                                                    }} >

                                                                    <TouchableOpacity onPress={() => { if (hasSubMenus) showSubMenu(element?.id) }} activeOpacity={hasSubMenus ? 0.5 : 1} style={{ flexDirection: 'row', alignItems: 'center', flex: 0.45 }}>
                                                                        {hasSubMenus ? (
                                                                            <ZenIcon
                                                                                type='entypo'
                                                                                name={isOpen ? "chevron-small-up" : "chevron-small-down"}
                                                                                size={22}
                                                                                color={theme.lightDark}
                                                                                onPress={() => {
                                                                                    showSubMenu(element?.id)
                                                                                }}
                                                                            />
                                                                        ) : null}
                                                                        <ZenText content={element?.menuName} style={{ fontSize: fontSize.mediumFontSize, fontFamily: appFonts.bold, color: theme.lightDark, marginLeft: hasSubMenus ? 5 : 0 }} />
                                                                    </TouchableOpacity>

                                                                    {!isGrantable ?
                                                                        <View style={{ flex: 0.55, alignItems: 'flex-end' }}>
                                                                            <ZenText content="Not available" style={{ fontSize: fontSize.smallFontSize, fontFamily: appFonts.medium, color: appColors.labelColor }} />
                                                                        </View> :
                                                                    <View style={{ flexDirection: 'row', columnGap: 5, flex: 0.55, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                                                        <ZenCheckBox
                                                                            containerStyle={{ borderWidth: 0, backgroundColor: 'transparent', margin: 0, padding: 0 }}
                                                                            size={18}
                                                                            disabled={!isRoleWrite} checkedIcon='check-square-o'
                                                                            unCheckedIcon='square-o'
                                                                            checkedColor={theme.primary}
                                                                            checked={hasSubMenus ? element?.selectAllRead : element?.isRead}
                                                                            onPress={() => {
                                                                                formik.setFieldTouched(`menuList${index}`, true)
                                                                                if (hasSubMenus) {
                                                                                    toggleParentSelectAllRead(index, !element?.selectAllRead);
                                                                                    if (!isOpen) {
                                                                                        showSubMenu(element?.id);
                                                                                    }
                                                                                } else {
                                                                                    toggleParentRead(index, !element?.isRead);
                                                                                }
                                                                            }}
                                                                            title="Read"
                                                                            titleStyle={{ fontSize: fontSize.smallFontSize, color: theme.lightDark }}
                                                                        />
                                                                        {parentHasWritePermission && (
                                                                            <ZenCheckBox
                                                                                containerStyle={{ borderWidth: 0, backgroundColor: 'transparent', margin: 0, padding: 0, marginLeft: 15 }}
                                                                                size={18}
                                                                                disabled={!isRoleWrite}
                                                                                checkedIcon='check-square-o'
                                                                                unCheckedIcon='square-o'
                                                                                checkedColor={theme.primary}
                                                                                checked={hasSubMenus ? element?.selectAllWrite : element?.isWrite}
                                                                                onPress={() => {
                                                                                    formik.setFieldTouched(`menuList${index}`, true)
                                                                                    if (hasSubMenus) {
                                                                                        toggleParentSelectAllWrite(index, !element?.selectAllWrite);
                                                                                        if (!isOpen) {
                                                                                            showSubMenu(element?.id);
                                                                                        }
                                                                                    } else {
                                                                                        toggleParentWrite(index, !element?.isWrite);
                                                                                    }
                                                                                }}
                                                                                title="Write"
                                                                                titleStyle={{ fontSize: fontSize.smallFontSize, color: theme.lightDark }}
                                                                            />
                                                                        )}
                                                                    </View>}
                                                                </View>
                                                                {
                                                                    hasSubMenus && element?.subMenus?.map((val: subMenuResProps, l: number) => {
                                                                        const isLastSub = l === (element.subMenus.length - 1);
                                                                        const isSubReadOnly = isReadOnlyMenu(val.menuName);
                                                                        return (
                                                                            isOpen &&
                                                                            <View
                                                                                key={l}
                                                                                style={{
                                                                                    flexDirection: 'row',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'space-between',
                                                                                    paddingVertical: 12,
                                                                                    paddingHorizontal: 16,
                                                                                    backgroundColor: '#FFFFFF',
                                                                                    borderBottomWidth: isLastSub ? 0 : 1,
                                                                                    borderBottomColor: '#F1F5F9'
                                                                                }}>
                                                                                <View style={{ flex: 0.45, paddingLeft: 10 }}>
                                                                                    <ZenText content={val.menuName} style={{
                                                                                        fontFamily: appFonts.medium,
                                                                                        color: appColors.lightDark, fontSize: fontSize.smallFontSize
                                                                                    }} />

                                                                                </View>
                                                                                {!hasPermissionTypes(val) ?
                                                                                    <View style={{ flex: 0.55, alignItems: 'flex-end' }}>
                                                                                        <ZenText content="Not available" style={{ fontFamily: appFonts.medium, color: appColors.labelColor, fontSize: fontSize.smallFontSize }} />
                                                                                    </View> :
                                                                                <View style={{ flexDirection: "row", columnGap: 5, flex: 0.55, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                                                                                    <ZenCheckBox
                                                                                        containerStyle={{
                                                                                            borderWidth: 0,
                                                                                            backgroundColor: 'transparent',
                                                                                            margin: 0,
                                                                                            padding: 0
                                                                                        }}
                                                                                        size={18}
                                                                                        disabled={!isRoleWrite}
                                                                                        checkedIcon='check-square-o'
                                                                                        unCheckedIcon='square-o'
                                                                                        checkedColor={theme.primary}
                                                                                        checked={val.isRead}
                                                                                        onPress={() => {
                                                                                            formik.setFieldTouched(`menuList${index}`, true)
                                                                                            toggleSubmenuRead(index, l, !val.isRead)
                                                                                        }}
                                                                                        title="Read"
                                                                                        titleStyle={{
                                                                                            fontFamily: appFonts.medium,
                                                                                            color: appColors.lightDark, fontSize: fontSize.smallFontSize
                                                                                        }}
                                                                                    />
                                                                                    {!isSubReadOnly && (
                                                                                        <ZenCheckBox
                                                                                            containerStyle={{
                                                                                                borderWidth: 0,
                                                                                                backgroundColor: 'transparent',
                                                                                                margin: 0,
                                                                                                padding: 0,
                                                                                                marginLeft: 15
                                                                                            }}
                                                                                            size={18}
                                                                                            disabled={!isRoleWrite}
                                                                                            checkedIcon='check-square-o'
                                                                                            unCheckedIcon='square-o'
                                                                                            checkedColor={theme.primary}
                                                                                            checked={val.isWrite}
                                                                                            onPress={() => {
                                                                                                formik.setFieldTouched(`menuList${index}`, true)
                                                                                                toggleSubmenuWrite(index, l, !val.isWrite)
                                                                                            }}
                                                                                            title="Write"
                                                                                            titleStyle={{
                                                                                                fontFamily: appFonts.medium,
                                                                                                color: appColors.lightDark, fontSize: fontSize.smallFontSize
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                                </View>}
                                                                            </View>
                                                                        )
                                                                    })
                                                                }
                                                            </View>
                                                        )
                                                    })
                                                }
                                            </View>
                                        </View>
                                    </View>
                                </ScrollView>
                            }
                        </View>

                        <Popover isVisible={dialogVisible}>
                            <CommonDialogBox type={'EDIT'} replaceValue='role' confirmation={setConfirmation} />
                        </Popover>
                    </KeyboardAvoidingView >
                )
            }}
        </ThemeConsumer >
    )
}

export default AddEditRole;

